import { StatusLight } from '@react-spectrum/s2';
import { useEffect, useRef, type ReactNode } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';

import type { ProcessSetupPanel as ProcessSetupPanelMessage } from '../../../../../../bridge/types/messages.ts';
import {
	isAlwaysOnOnlyProcess,
	isMonitorOnlyProcess,
	isOwnerOnlyProcess,
	isSkipped,
	nonSkippedActivatableArtifactCount,
	setupCardItems,
} from '../../../../../../bridge/utils/setupCardItems.ts';
import { ArtifactCard } from '../ArtifactCard.tsx';
import { MessageRow } from '../MessageRow.tsx';
import { getPanelIcon } from '../panelTypeIcons.tsx';

import { SetupCard } from './SetupCard.tsx';

export const CampaignSetupPanel = ({
	message,
	onClick,
	icon,
}: {
	message: ProcessSetupPanelMessage;
	onClick?: () => void;
	icon?: ReactNode;
}) => {
	const bridgeApi = useBridgeApi();
	const processId = message.process_id;
	const snapshot = bridgeApi.user_state?.process_setup_snapshots?.[processId];
	const error = bridgeApi.user_state?.process_setup_errors?.[processId];
	// The server threw this Process away, because a plan edit made the campaign stale. Read FIRST,
	// and short-circuit before the load effect: `loadRequestedRef` below latches on the first load
	// and is never reset, so dropping the snapshot alone would strand this card as a skeleton, and
	// a remount would 404 into the error box. One true sentence beats both.
	const discarded =
		bridgeApi.user_state?.process_setup_discarded?.[processId] === true;
	// A discarded campaign is normally being rebuilt. One the owner DELETED is not, and the
	// rebuild sentence would promise work nothing will do.
	const removed =
		bridgeApi.user_state?.process_setup_removed?.[processId] === true;

	const loadRequestedRef = useRef(false);

	useEffect(() => {
		if (discarded || snapshot || error || loadRequestedRef.current) return;
		loadRequestedRef.current = true;
		bridgeApi.sendAction({
			type: 'do_load_setup_snapshot',
			process_id: processId,
		});
	}, [bridgeApi, processId, snapshot, error, discarded]);

	if (discarded) {
		return (
			<MessageRow>
				<div
					data-testid={
						removed ? 'campaign-setup-removed' : 'campaign-setup-discarded'
					}
					className="glass-card-sm my-2 p-4"
				>
					<p className="text-sm text-(--text-secondary)">
						{removed
							? 'This campaign was removed from your plan.'
							: 'This campaign is being set up again, from your edited plan.'}
					</p>
				</div>
			</MessageRow>
		);
	}

	// Several unrelated failures write this one row — a load, a skip and a
	// refused activation — so the wording comes from the row's own `kind`.
	// Guessing the cause from snapshot presence tells an owner who pressed Skip
	// that their campaign refused to start.
	const errorHeadline =
		error?.kind === 'activation'
			? 'Couldn’t start this campaign'
			: error?.kind === 'skip'
				? 'Couldn’t update this campaign'
				: 'Couldn’t load campaign setup';
	const errorBlock = error ? (
		<div
			data-testid="campaign-setup-error"
			data-error-kind={error.kind}
			data-error-status={error.status}
			data-process-id={processId}
			className="my-2 rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm"
		>
			<p className="text-sm font-medium text-red-700">{errorHeadline}</p>
			<p className="mt-1 text-xs text-red-600">{error.message}</p>
		</div>
	) : null;

	if (!snapshot) {
		if (errorBlock) {
			return <MessageRow>{errorBlock}</MessageRow>;
		}
		return (
			<MessageRow>
				<div className="flex w-full flex-col gap-3 rounded-xl bg-gray-100 p-4">
					<div className="h-5 w-40 animate-pulse rounded bg-gray-100" />
					<div className="h-16 w-full animate-pulse rounded bg-gray-100" />
					<div className="h-16 w-full animate-pulse rounded bg-gray-100" />
				</div>
			</MessageRow>
		);
	}

	const baseTitle = (snapshot.title ?? '').trim();
	const description = (snapshot.description ?? '').trim();
	const children = snapshot.children ?? [];
	const postActivation =
		snapshot.status !== 'proposed' &&
		snapshot.status !== 'draft' &&
		snapshot.status !== 'processing';

	// Once a campaign is live, the per-step setup rows are gone (nothing left to
	// configure). The card becomes a single clickable tile that opens the
	// monitor/campaign detail panel — but only when a click handler was supplied.
	// A monitor with a single comment-watcher resolves downstream to its editable
	// panel; the routing decision is made from process content, not from this click.
	if (postActivation && onClick) {
		// A card surfaced straight from a process_setup_panel message carries no
		// icon, so fall back to the same icon its panel_artifact sibling shows:
		// a single-item process activates as a "job", anything larger as a
		// "campaign" (see the activation panel_artifact in bridge process-setup).
		// Without this the card renders an empty 40x40 icon slot.
		const defaultIcon = getPanelIcon(
			children.length === 1 ? 'job' : 'campaign',
		);
		return (
			<>
				{errorBlock && <MessageRow>{errorBlock}</MessageRow>}
				<ArtifactCard
					icon={icon ?? defaultIcon}
					title={baseTitle || 'Untitled'}
					description={description}
					onClick={onClick}
				/>
			</>
		);
	}

	const emailArtifacts = children.filter(
		(c) => c.type === 'email_html' || c.type === 'email_plaintext',
	);
	const totalEmails = emailArtifacts.length;
	const postArtifacts = children.filter((c) => c.type === 'instagram_post');
	const totalPosts = postArtifacts.length;

	const items = setupCardItems(snapshot);
	// Recipients/Schedule rows follow the SAME setupCardItems derivation the rest of
	// setup uses (surviving-action gating), so skipping the only email drops both
	// rows. Reading raw actions here was the ADO-1169 v2 bug.
	const showRecipientsRow = items.some((i) => i.kind === 'recipients');
	// The skip affordance and the "at least one item must remain" guard count the
	// activatable artifacts (publishing pieces plus monitors), which is the same
	// set the server enforces on activation.
	const activatableIds = new Set(
		items.filter((i) => i.kind === 'artifact').map((i) => i.id),
	);
	const activatableCount = activatableIds.size;
	const nonSkipped = nonSkippedActivatableArtifactCount(snapshot, items);
	const hasScheduleItem = items.some((i) => i.kind === 'schedule');
	// A single derived mode rather than two independent booleans, so this and
	// every other consumer (ProcessSchedulePanel, SetupCard) resolve the
	// (mutually-exclusive-by-construction) modes the same way.
	const reviewMode: 'ownerOnly' | 'monitorOnly' | 'alwaysOnOnly' | 'normal' =
		isOwnerOnlyProcess(snapshot)
			? 'ownerOnly'
			: isMonitorOnlyProcess(snapshot)
				? 'monitorOnly'
				: isAlwaysOnOnlyProcess(snapshot)
					? 'alwaysOnOnly'
					: 'normal';
	const showScheduleRow = !postActivation && hasScheduleItem;
	const showStatus = hasScheduleItem || postActivation;

	return (
		<MessageRow>
			{/* MessageRow lays its children out in a row, so the error and the card
			    need a column of their own to stack in. */}
			<div className="flex w-full flex-col">
				{errorBlock}
				<div
					data-testid="campaign-setup-card"
					data-process-id={snapshot.process_id}
					className="flex w-full flex-col gap-5 rounded-xl bg-gray-100 p-4"
				>
					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between gap-2">
							<div className="flex min-w-0 items-baseline gap-1.5 truncate">
								<span className="truncate text-base font-semibold text-gray-900">
									{baseTitle || 'Untitled'}
								</span>
							</div>
							{showStatus && (
								<StatusLight variant={postActivation ? 'positive' : 'neutral'}>
									{postActivation ? 'Active' : 'Draft'}
								</StatusLight>
							)}
						</div>
						{description && (
							<p className="line-clamp-4 text-sm text-gray-500">
								{description}
							</p>
						)}
					</div>
					{!postActivation && (
						<div className="flex flex-col gap-1 rounded-xl bg-white p-2">
							{children.map((artifact) => {
								const isEmail =
									artifact.type === 'email_html' ||
									artifact.type === 'email_plaintext';
								const emailIndex = isEmail
									? emailArtifacts.findIndex((e) => e.id === artifact.id) + 1
									: undefined;
								const isPost = artifact.type === 'instagram_post';
								const postIndex = isPost
									? postArtifacts.findIndex((p) => p.id === artifact.id) + 1
									: undefined;
								return (
									<SetupCard
										key={artifact.id}
										kind="artifact"
										snapshot={snapshot}
										artifact={artifact}
										fallbackTitle="Item"
										emailIndex={emailIndex}
										emailTotal={isEmail ? totalEmails : undefined}
										postIndex={postIndex}
										postTotal={isPost ? totalPosts : undefined}
										isLocked={postActivation}
										showSkip={
											activatableIds.has(artifact.id) && activatableCount >= 2
										}
										canSkip={
											!(nonSkipped === 1 && !isSkipped(snapshot, artifact.id))
										}
										skipDisabledReason="At least one item must remain"
									/>
								);
							})}
							{/* Actions that are work in their own right — an owner ask carries
						    its instruction on the action, with no artifact to render a row
						    on its behalf. setupCardItems already emits these; without the
						    loop the item is invisible while still gating go-live. */}
							{items
								.filter((i) => i.kind === 'action')
								.map((item) => {
									const ownerAction = (snapshot.actions ?? []).find(
										(a) => a.id === item.id,
									);
									if (ownerAction?.type !== 'request_owner_action') return null;
									return (
										<SetupCard
											key={ownerAction.id}
											kind="action"
											snapshot={snapshot}
											action={ownerAction}
											isLocked={postActivation}
											showSkip
										/>
									);
								})}
							{showRecipientsRow && (
								<SetupCard
									kind="recipients"
									processId={processId}
									isFinalized={snapshot.recipients_finalized === true}
									isLocked={postActivation}
								/>
							)}
							{showScheduleRow && (
								<SetupCard
									kind="schedule"
									snapshot={snapshot}
									reviewMode={reviewMode}
								/>
							)}
						</div>
					)}
				</div>
			</div>
		</MessageRow>
	);
};
